import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../utils/cn';
import Icon from '../../../../components/AppIcon';
import {
  UserSkillView, SkillEvidenceView, SkillEvidenceType, ProficiencyLevel,
  UserStrengthView, SkillEvidenceSourceType,
} from '../../../../models/employee';
import EvidenceTimeline, { TimelineItem } from './EvidenceTimeline';
import { format, parseISO } from 'date-fns';
import { getSkillEvidence, editUserSkill, getUserStrengthEvidence } from '../../../../api/Employee/skills';

/*
 * CO-447 — the third copy of the local `Strength` interface lived here, byte-identical to the two
 * in `../index.tsx` and `./StrengthsSection.tsx`. All three are replaced by `UserStrengthView`.
 */

// Which timeline lane each derived-evidence source renders in. `EvidenceTimeline` already styles
// all five lanes; `endorsement` (emerald) has simply never had a row to render until now.
const SOURCE_TIMELINE_TYPE: Record<SkillEvidenceSourceType, TimelineItem['type']> = {
  Recognition: 'feedback',
  Goal: 'milestone',
  GoalUpdate: 'milestone',
  CheckIn: 'checkin',
  TeamHubPost: 'feedback',
  TeamHubPostComment: 'feedback',
  ManagerReflectionResponse: 'feedback',
  ManualEndorsement: 'endorsement',
  Unknown: 'evidence',
};

const SOURCE_TIMELINE_ICON: Record<SkillEvidenceSourceType, string> = {
  Recognition: 'Award',
  Goal: 'Target',
  GoalUpdate: 'TrendingUp',
  CheckIn: 'MessageCircle',
  TeamHubPost: 'Megaphone',
  TeamHubPostComment: 'MessageSquare',
  ManagerReflectionResponse: 'NotebookPen',
  ManualEndorsement: 'ThumbsUp',
  Unknown: 'FileText',
};

const sourceKey = (source: SkillEvidenceSourceType) =>
  `strengths.evidence.source.${source.charAt(0).toLowerCase()}${source.slice(1)}`;

interface RoleHistory {
  id: string;
  title: string;
  department: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: 'current' | 'past';
}

interface SkillStrengthDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  item: UserSkillView | UserStrengthView | null;
  type: 'skill' | 'strength';
  roleHistory?: RoleHistory[];
  onSkillUpdated?: (updatedSkill: UserSkillView) => void;
}

const SkillStrengthDetailDrawer: React.FC<SkillStrengthDetailDrawerProps> = ({
  open,
  onClose,
  item,
  type,
  roleHistory = [],
  onSkillUpdated
}) => {
  const { t } = useTranslation();
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingProficiency, setIsUpdatingProficiency] = useState(false);
  const [showProficiencyDropdown, setShowProficiencyDropdown] = useState(false);

  const isSkill = type === 'skill';
  const skillItem = item as UserSkillView;
  const strengthItem = item as UserStrengthView;

  // Map SkillEvidenceType to TimelineItem type
  const mapEvidenceTypeToTimelineType = (evidenceType: SkillEvidenceType): TimelineItem['type'] => {
    switch (evidenceType) {
      case SkillEvidenceType.SkillAdded:
        return 'milestone';
      case SkillEvidenceType.Endorsement:
        return 'endorsement';
      case SkillEvidenceType.ProficiencyChange:
        return 'milestone';
      case SkillEvidenceType.Feedback:
        return 'feedback';
      default:
        return 'evidence';
    }
  };

  // Map SkillEvidenceType to icon
  const mapEvidenceTypeToIcon = (evidenceType: SkillEvidenceType): string => {
    switch (evidenceType) {
      case SkillEvidenceType.SkillAdded:
        return 'Flag';
      case SkillEvidenceType.Endorsement:
        return 'ThumbsUp';
      case SkillEvidenceType.ProficiencyChange:
        return 'TrendingUp';
      case SkillEvidenceType.Feedback:
        return 'MessageCircle';
      default:
        return 'FileText';
    }
  };

  // Fetch evidence when drawer opens
  useEffect(() => {
    const fetchEvidence = async () => {
      if (!open || !isSkill || !skillItem?.id) return;

      setIsLoading(true);
      try {
        const result = await getSkillEvidence(skillItem.id);
        if (result.status === 200 && result.payload) {
          const mappedItems: TimelineItem[] = result.payload.map((evidence: SkillEvidenceView) => ({
            id: `evidence_${evidence.id}`,
            type: mapEvidenceTypeToTimelineType(evidence.evidenceType),
            icon: mapEvidenceTypeToIcon(evidence.evidenceType),
            title: evidence.title,
            description: evidence.description || '',
            date: new Date(evidence.evidenceDate).toISOString(),
            author: evidence.authorName || 'System',
            _evidenceType: evidence.evidenceType
          }));
          // Sort items: SkillAdded first, then by date descending
          const sortedItems = mappedItems.sort((a, b) => {
            const aIsSkillAdded = (a as any)._evidenceType === SkillEvidenceType.SkillAdded;
            const bIsSkillAdded = (b as any)._evidenceType === SkillEvidenceType.SkillAdded;
            if (aIsSkillAdded && !bIsSkillAdded) return -1;
            if (!aIsSkillAdded && bIsSkillAdded) return 1;
            return new Date(b.date).getTime() - new Date(a.date).getTime();
          });
          setTimelineItems(sortedItems);
        }
      } catch (error) {
        console.error('Failed to fetch skill evidence:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvidence();
  }, [open, isSkill, skillItem?.id]);

  /*
   * CO-447 — REAL derivation, not a mock.
   *
   * This effect used to fabricate a single "Strength Confirmed" row from the strength object
   * itself, with a hardcoded English title and description. It could never be wrong because it
   * asserted nothing: it restated the row it was handed. The drawer now shows the evidence the
   * detector actually cited, which is the only thing that makes a derived strength auditable.
   */
  useEffect(() => {
    const fetchStrengthEvidence = async () => {
      if (!open || isSkill || !strengthItem?.id) return;
      setIsLoading(true);
      try {
        const result = await getUserStrengthEvidence(strengthItem.id);
        if (result.status === 200 && result.payload) {
          const mapped: TimelineItem[] = result.payload.map((e) => ({
            id: `strengthEvidence_${e.id}`,
            type: SOURCE_TIMELINE_TYPE[e.sourceType] ?? 'evidence',
            icon: SOURCE_TIMELINE_ICON[e.sourceType] ?? 'FileText',
            title: t(sourceKey(e.sourceType)),
            description: e.quotedSpan || '',
            date: new Date(e.occurredAt).toISOString(),
            author: e.authorName || undefined,
          }));
          mapped.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          setTimelineItems(mapped);
        } else {
          // 403 is legitimate here: the quoted spans are restricted to the subject, their manager
          // and HR. An empty timeline claims "no evidence", which is a different statement.
          setTimelineItems([]);
        }
      } catch (error) {
        console.error('Failed to fetch strength evidence:', error);
        setTimelineItems([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStrengthEvidence();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isSkill, strengthItem?.id]);

  if (!item) return null;

  const handleTimelineItemClick = (_timelineItem: TimelineItem) => {
    // TODO: implement timeline item detail view
  };

  // §3.3.5, same defect as SkillsSection: these labels were keyed on the raw English enum member
  // (`t('Awareness')`), a key no locale file carries, so i18next echoed it back and the dropdown
  // read English on every locale. Value stays raw — it is what EditUserSkill receives.
  const proficiencyLevels = [
    { value: ProficiencyLevel.Awareness, label: t('skills.proficiency.awareness'), description: t('Basic understanding') },
    { value: ProficiencyLevel.Working, label: t('skills.proficiency.working'), description: t('Can apply with guidance') },
    { value: ProficiencyLevel.Strong, label: t('skills.proficiency.strong'), description: t('Expert level mastery') }
  ];

  const handleProficiencyChange = async (newProficiency: ProficiencyLevel) => {
    if (!isSkill || !skillItem) return;

    setIsUpdatingProficiency(true);
    setShowProficiencyDropdown(false);

    try {
      const result = await editUserSkill(skillItem.id, {
        id: skillItem.id,
        userId: skillItem.userId,
        skillName: skillItem.skillName,
        proficiency: newProficiency
      });

      if (result.status === 200 && result.payload) {
        onSkillUpdated?.(result.payload);
        // Refresh evidence timeline to show the proficiency change
        const evidenceResult = await getSkillEvidence(skillItem.id);
        if (evidenceResult.status === 200 && evidenceResult.payload) {
          const mappedItems: TimelineItem[] = evidenceResult.payload.map((evidence: SkillEvidenceView) => ({
            id: `evidence_${evidence.id}`,
            type: mapEvidenceTypeToTimelineType(evidence.evidenceType),
            icon: mapEvidenceTypeToIcon(evidence.evidenceType),
            title: evidence.title,
            description: evidence.description || '',
            date: new Date(evidence.evidenceDate).toISOString(),
            author: evidence.authorName || 'System',
            _evidenceType: evidence.evidenceType
          }));
          const sortedItems = mappedItems.sort((a, b) => {
            const aIsSkillAdded = (a as any)._evidenceType === SkillEvidenceType.SkillAdded;
            const bIsSkillAdded = (b as any)._evidenceType === SkillEvidenceType.SkillAdded;
            if (aIsSkillAdded && !bIsSkillAdded) return -1;
            if (!aIsSkillAdded && bIsSkillAdded) return 1;
            return new Date(b.date).getTime() - new Date(a.date).getTime();
          });
          setTimelineItems(sortedItems);
        }
      }
    } catch (error) {
      console.error('Failed to update proficiency:', error);
    } finally {
      setIsUpdatingProficiency(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        />
      )}
      {/* Panel */}
      <div className={cn(
        "fixed inset-y-0 end-0 z-50 flex w-full flex-col bg-muted transition-transform duration-300 ease-in-out md:w-[720px] lg:w-[900px]",
        open ? "translate-x-0" : "translate-x-full rtl:-translate-x-full"
      )}>
        <div className="h-full flex flex-col">
          {/* Header Section */}
          <div className="bg-card border-b border-border p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={onClose}
                className="flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="ArrowLeft" size={16} className="me-1" />
                {t('Back')}
              </button>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-muted transition-colors"
                aria-label={t('Close')}
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>

            {/* Item Header Card */}
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                isSkill ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-purple-100 dark:bg-purple-900/30'
              }`}>
                <Icon
                  name={isSkill ? 'Award' : 'Sparkles'}
                  size={24}
                  className={isSkill ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground truncate">
                    {isSkill ? skillItem.skillName : strengthItem.label}
                  </h2>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    isSkill ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                  }`}>
                    {isSkill ? t('Skill') : t('Strength')}
                  </span>
                </div>

                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  {isSkill ? (
                    <>
                      <div className="relative">
                        <button
                          onClick={() => setShowProficiencyDropdown(!showProficiencyDropdown)}
                          className="flex items-center hover:bg-muted rounded-md px-2 py-1 -ms-2 transition-colors"
                          disabled={isUpdatingProficiency}
                        >
                          <Icon name="TrendingUp" size={16} className="me-1" />
                          {t('Proficiency')}: <strong className="ms-1">{t(`skills.proficiency.${(skillItem.proficiency || 'Awareness').toLowerCase()}`)}</strong>
                          {isUpdatingProficiency ? (
                            <div className="ms-2 animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                          ) : (
                            <Icon name="ChevronDown" size={14} className="ms-1 text-muted-foreground" />
                          )}
                        </button>
                        {showProficiencyDropdown && (
                          <div className="absolute top-full start-0 mt-1 bg-card rounded-lg shadow-lg border border-border py-1 z-50 min-w-[200px]">
                            {proficiencyLevels.map((level) => (
                              <button
                                key={level.value}
                                onClick={() => handleProficiencyChange(level.value)}
                                className={`w-full text-start px-4 py-2 hover:bg-muted transition-colors ${
                                  skillItem.proficiency === level.value ? 'bg-blue-50 dark:bg-blue-950/30' : ''
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-foreground">{level.label}</span>
                                  {skillItem.proficiency === level.value && (
                                    <Icon name="Check" size={16} className="text-primary" />
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">{level.description}</p>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {skillItem.reportedByName && (
                        <span className="flex items-center">
                          <Icon name="User" size={16} className="me-1" />
                          {t('Added by')} {skillItem.reportedByName}
                        </span>
                      )}
                      <span className="flex items-center">
                        <Icon name="Link" size={16} className="me-1" />
                        {timelineItems.length} {t('evidence items')}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="flex items-center">
                        <Icon name="CheckCircle" size={16} className="me-1" />
                        <strong>{t(`strengths.confidence.${(strengthItem.confidence ?? 'Low').toLowerCase()}`)}</strong>
                      </span>
                      <span className="flex items-center">
                        <Icon name="Users" size={16} className="me-1" />
                        {t('strengths.byAuthors', { count: strengthItem.distinctAuthorCount ?? 0 })}
                      </span>
                      <span className="flex items-center">
                        <Icon name="Calendar" size={16} className="me-1" />
                        {t('strengths.lastConfirmed')} {strengthItem.lastConfirmedAt
                          ? format(parseISO(strengthItem.lastConfirmedAt), 'MMM d, yyyy')
                          : ''}
                      </span>
                      <span className="flex items-center">
                        <Icon name="Link" size={16} className="me-1" />
                        {strengthItem.evidenceCount ?? 0} {t('evidence items')}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Content - 2 Column Layout on Desktop */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* Left Column - Evidence Timeline (2/3 width on desktop) */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-card rounded-xl shadow-sm border border-border p-4 md:p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="Clock" size={20} className="me-2 text-muted-foreground" />
                    {t('Evidence Timeline')}
                  </h3>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : timelineItems.length > 0 ? (
                    <EvidenceTimeline
                      items={timelineItems}
                      onItemClick={handleTimelineItemClick}
                    />
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="Clock" size={32} className="mx-auto mb-2 text-muted-foreground/50" />
                      <p>{t('No evidence recorded yet')}</p>
                      <p className="text-sm mt-1">{t('Evidence will appear here as the skill develops')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Linked Items (1/3 width on desktop) */}
              <div className="space-y-6">

                {/* Development Objectives Section - Placeholder for future */}
                <div className="bg-card rounded-xl shadow-sm border border-border p-4 md:p-6">
                  <h3 className="text-base font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="Target" size={18} className="me-2 text-muted-foreground" />
                    {t('Development Objectives')}
                  </h3>
                  <div className="text-center py-4 text-muted-foreground">
                    <Icon name="Target" size={24} className="mx-auto mb-2 text-muted-foreground/50" />
                    <p className="text-sm">{t('No linked objectives')}</p>
                    <p className="text-xs mt-1 text-muted-foreground">{t('Link objectives to track development')}</p>
                  </div>
                </div>

                {/* Role History Section */}
                <div className="bg-card rounded-xl shadow-sm border border-border p-4 md:p-6">
                  <h3 className="text-base font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="Briefcase" size={18} className="me-2 text-muted-foreground" />
                    {t('Role History')}
                  </h3>
                  {roleHistory.length > 0 ? (
                    <div className="space-y-3">
                      {roleHistory.map((role) => (
                        <div
                          key={role.id}
                          className="border border-border rounded-lg p-3"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <p className="text-sm font-medium text-foreground">{role.title}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              role.status === 'current'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {role.status === 'current' ? t('current') : role.status}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">{role.department}</p>
                          <div className="text-xs text-muted-foreground">
                            <span>{format(parseISO(role.effectiveFrom), 'MMM yyyy')}</span>
                            <span className="mx-1">-</span>
                            <span>{role.effectiveTo ? format(parseISO(role.effectiveTo), 'MMM yyyy') : t('Present')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      <Icon name="Briefcase" size={24} className="mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm">{t('No role history')}</p>
                    </div>
                  )}
                </div>

                {/* Definition Card */}
                <div className="bg-gradient-to-br from-blue-50 dark:from-blue-950/20 to-indigo-50 dark:to-indigo-950/20 rounded-xl border border-blue-200 dark:border-blue-800 p-4 md:p-6">
                  <h3 className="text-base font-semibold text-foreground mb-3 flex items-center">
                    <Icon name="BookOpen" size={18} className="me-2 text-blue-600 dark:text-blue-400" />
                    {t('Definition')}
                  </h3>
                  <p className="text-sm text-foreground leading-relaxed">
                    {isSkill
                      ? t('{{name}} represents a capability that can be developed through practice, learning, and experience. Proficiency levels indicate current mastery.', { name: skillItem.skillName })
                      : t('{{name}} is a recurring pattern of positive contribution that emerges consistently across different contexts and situations, confirmed through observed evidence.', { name: strengthItem.label })
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SkillStrengthDetailDrawer;
